import { PackageSearch, Plus, RefreshCw } from 'lucide-react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ScreenHeader } from '../components/AppShell';
import { OrderCard } from '../components/OrderCard';
import { SearchBar } from '../components/SearchBar';
import {
  StatusTabs,
  availableStatuses,
  statusesForTab,
  type StatusTab,
} from '../components/StatusTabs';
import { StoreFilter } from '../components/StoreFilter';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ListSkeleton } from '../components/ui/Skeleton';
import { errorMessage } from '../lib/errors';
import { useI18n } from '../lib/i18n';
import { useLive } from '../lib/live';
import { useOrderStatusCounts, useOrders } from '../lib/queries';
import { useSession } from '../lib/session';
import { haptics } from '../lib/telegram';
import { useDemo, maskStoreName } from '../lib/demo';

/** Высота карточки до первого измерения: близко к реальной, чтобы не дёргалось. */
const ESTIMATED_CARD_HEIGHT = 148;

export function OrdersScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { selectedStores, storeById, activeStores, stores, can } = useSession();
  const { lastEvent } = useLive();
  const { isDemo } = useDemo();

  // Кнопку создания показываем, только если есть куда создавать.
  const canCreateOrder =
    can('orders:create') && stores.some((store) => store.capabilities.createOrder);

  const [tab, setTab] = useState<StatusTab>('all');
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({ stores: selectedStores, status: statusesForTab(tab), search }),
    [selectedStores, tab, search],
  );

  const query = useOrders(filters);
  const tabStatuses = useMemo(() => availableStatuses(activeStores), [activeStores]);
  const { counts } = useOrderStatusCounts(selectedStores, tabStatuses);
  const totalCount = useMemo(
    () => tabStatuses.reduce((sum, status) => sum + (counts[status] ?? 0), 0),
    [tabStatuses, counts],
  );
  const orders = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);
  const partial = query.data?.pages.at(-1)?.partial ?? [];

  const listRef = useRef<HTMLDivElement>(null);
  const [listTop, setListTop] = useState(0);
  useEffect(() => {
    setListTop(listRef.current?.offsetTop ?? 0);
  }, [orders.length]);

  // Виртуализация по окну: прокручивается вся страница, а не внутренний блок,
  // поэтому и шапка Telegram, и жесты ведут себя как в нативном списке.
  const virtualizer = useWindowVirtualizer({
    count: orders.length,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    overscan: 6,
    scrollMargin: listTop,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Догружаем следующую страницу заранее, за несколько карточек до конца.
  useEffect(() => {
    const last = virtualItems.at(-1);
    if (!last) return;
    if (last.index >= orders.length - 5 && query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  }, [virtualItems, orders.length, query]);

  const highlightRef = lastEvent && 'ref' in lastEvent ? lastEvent.ref : null;

  return (
    <>
      <ScreenHeader
        title={t('orders.title')}
        subtitle={activeStores.length === 1 ? maskStoreName(activeStores[0]?.label, isDemo) : undefined}
        actions={
          <>
            <StoreFilter />
            <Button
              variant="secondary"
              size="icon"
              aria-label={t('app.retry')}
              disabled={query.isRefetching}
              onClick={() => {
                haptics.tap();
                void query.refetch();
              }}
            >
              <RefreshCw size={16} className={query.isRefetching ? 'animate-spin' : undefined} />
            </Button>
            {canCreateOrder && (
              <Button
                size="icon"
                aria-label={t('newOrder.title')}
                haptic="press"
                onClick={() => void navigate('/orders/new')}
              >
                <Plus size={18} />
              </Button>
            )}
          </>
        }
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder={t('orders.searchPlaceholder')}
        />
        <StatusTabs
          value={tab}
          onChange={setTab}
          stores={activeStores}
          counts={counts}
          total={totalCount}
        />
      </ScreenHeader>

      <div className="px-4 pt-3">
        {partial.length > 0 && (
          <p className="mb-2 rounded-xl bg-[var(--tone-amber-bg)] px-3 py-2 text-[13px] text-[var(--tone-amber)]">
            {t('orders.partial')}: {partial.map((failure) => failure.storeId).join(', ')}
          </p>
        )}

        {query.isPending && <ListSkeleton />}

        {query.isError && !query.isPending && (
          <EmptyState
            title={errorMessage(query.error, t)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
                {t('app.retry')}
              </Button>
            }
          />
        )}

        {!query.isPending && !query.isError && orders.length === 0 && (
          <EmptyState
            icon={<PackageSearch size={40} />}
            title={search || tab !== 'all' ? t('orders.emptyFiltered') : t('orders.empty')}
          />
        )}

        {orders.length > 0 && (
          <div
            ref={listRef}
            className="relative"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualItems.map((virtualItem) => {
              const order = orders[virtualItem.index];
              if (!order) return null;

              return (
                <div
                  key={order.ref}
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                  className="absolute top-0 left-0 w-full pb-2"
                  style={{ transform: `translateY(${virtualItem.start - listTop}px)` }}
                >
                  <OrderCard
                    order={order}
                    store={storeById(order.storeId)}
                    highlight={highlightRef === order.ref}
                  />
                </div>
              );
            })}
          </div>
        )}

        {query.isFetchingNextPage && (
          <div className="pt-2">
            <ListSkeleton count={2} />
          </div>
        )}
      </div>
    </>
  );
}
